import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  Calendar,
  CheckCircle2,
  Clock,
  ExternalLink,
  MessageSquare,
  PackageCheck,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  User,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import "./FreelancerFiverr.css";
import {
  formatServiceCurrency,
  getFreelancerServiceDetails,
  getFreelancerServiceTitle,
  getMyServiceOrders,
  getMyFreelancerOrders,
  isFreelancerServiceAvailable,
} from "../api/freelancerServices";

function getStatusBadge(status, isAr) {
  const map = {
    pending: { className: "bg-warning text-dark", label: isAr ? "قيد الانتظار" : "Pending" },
    paid: { className: "bg-primary text-white", label: isAr ? "مدفوع" : "Paid" },
    in_progress: { className: "bg-info text-dark", label: isAr ? "جاري التنفيذ ⏱️" : "In Progress ⏱️" },
    delivered: { className: "bg-success text-white", label: isAr ? "تم التسليم 📦" : "Work Delivered 📦" },
    revision_requested: { className: "bg-danger text-white", label: isAr ? "طلب تعديل 🔄" : "Revision Requested 🔄" },
    completed: { className: "bg-success text-white", label: isAr ? "مكتمل 🎉" : "Completed 🎉" },
    cancelled: { className: "bg-secondary text-white", label: isAr ? "ملغي" : "Cancelled" },
    disputed: { className: "bg-danger text-white", label: isAr ? "نزاع قائم" : "Disputed" },
  };

  return map[String(status || "").toLowerCase()] || {
    className: "bg-secondary text-white",
    label: status || (isAr ? "غير معروف" : "Unknown"),
  };
}

export default function MyServiceOrders() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("client"); // "client" or "freelancer"
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [reorderLoadingId, setReorderLoadingId] = useState(null);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const fetcher = activeTab === "freelancer" ? getMyFreelancerOrders : getMyServiceOrders;
        const data = await fetcher().catch(() => []);

        if (active) setOrders(Array.isArray(data) ? data : []);
      } catch (err) {
        logger.error("Service orders load error", err);
        if (active) setError(isAr ? "تعذر تحميل قائمة الطلبات." : "Unable to load service orders.");
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [activeTab, isAr]);

  const filteredOrders = orders.filter((ord) => {
    if (statusFilter === "all") return true;
    return String(ord.status || "").toLowerCase() === statusFilter;
  });

  return (
    <div className="fv-marketplace-wrapper" dir={isAr ? "rtl" : "ltr"}>
      <div className="fv-container py-4 py-lg-5">
        {/* Header Title */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <div>
            <h1 className="fw-extrabold fs-2 m-0 text-body">
              {isAr ? "مركز إدارة طلبات الخدمات (Orders Hub)" : "Freelancer Orders Hub"}
            </h1>
            <p className="text-muted small mt-1 m-0">
              {isAr
                ? "تابع تقدم العمل، العداد التنازلي للتسليم، والتواصل المباشر في مساحة العمل."
                : "Manage project progress, live delivery countdowns, and order workspaces."}
            </p>
          </div>
          <Link to="/freelancers" className="fv-search-box__btn rounded-3">
            <span>{isAr ? "تصفح الخدمات" : "Browse Gigs"}</span>
            <ArrowRight size={16} className={isAr ? "rotate-180" : ""} />
          </Link>
        </div>

        {/* Fiverr Dual Tabs: Client vs Freelancer */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 border-bottom pb-3">
          <div className="d-flex gap-2">
            <button
              type="button"
              className={`fv-chip ${activeTab === "client" ? "is-active" : ""}`}
              onClick={() => setActiveTab("client")}
            >
              <PackageCheck size={16} />
              <span>{isAr ? "طلباتي كعميل (My Purchases)" : "My Purchases (Client)"}</span>
            </button>

            <button
              type="button"
              className={`fv-chip ${activeTab === "freelancer" ? "is-active" : ""}`}
              onClick={() => setActiveTab("freelancer")}
            >
              <Briefcase size={16} />
              <span>{isAr ? "طلباتي الواردة كمستقل (Freelancer Gigs)" : "Incoming Orders (Freelancer)"}</span>
            </button>
          </div>

          {/* Status Filter */}
          <div className="d-flex align-items-center gap-2">
            <small className="text-muted font-bold text-uppercase">{isAr ? "الحالة:" : "Status:"}</small>
            <select
              className="fv-filter-select py-1 px-3"
              style={{ width: "auto" }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">{isAr ? "جميع الطلبات" : "All Orders"}</option>
              <option value="in_progress">{isAr ? "جاري التنفيذ" : "In Progress"}</option>
              <option value="delivered">{isAr ? "تم التسليم" : "Delivered"}</option>
              <option value="completed">{isAr ? "مكتمل" : "Completed"}</option>
              <option value="cancelled">{isAr ? "ملغي" : "Cancelled"}</option>
            </select>
          </div>
        </div>

        {/* Content Body */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-success mb-3" style={{ width: "3rem", height: "3rem" }} />
            <p className="fw-semibold text-muted">{isAr ? "جاري تحميل الطلبات..." : "Loading orders..."}</p>
          </div>
        ) : error ? (
          <div className="fv-state-box">
            <h3 className="fv-state-box__title">{error}</h3>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="fv-state-box">
            <div className="fv-state-box__icon">
              <PackageCheck size={32} />
            </div>
            <h3 className="fv-state-box__title">
              {isAr ? "لا توجد طلبات في هذه القائمة بعد" : "No orders found in this view"}
            </h3>
            <p className="fv-state-box__desc">
              {isAr
                ? "يمكنك البدء بتصفح سوق المستقلين واختيار الخدمة المناسبة لمشروعك."
                : "Explore top freelancer gigs and order your next project."}
            </p>
            <Link to="/freelancers" className="fv-search-box__btn mx-auto">
              {isAr ? "تصفح خدمات المستقلين" : "Explore Marketplace"}
            </Link>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {filteredOrders.map((ord) => {
              const service = ord.service || {};
              const badge = getStatusBadge(ord.status, isAr);
              const title = ord.title || getFreelancerServiceTitle(service, isAr);
              const otherParty = activeTab === "freelancer" ? ord.client?.name : ord.freelancer?.name;

              return (
                <article key={ord.id} className="card border-0 shadow-sm rounded-4 p-4 bg-body">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <img
                        src={
                          service?.cover_image ||
                          "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80"
                        }
                        alt={title}
                        className="rounded-3 object-fit-cover"
                        style={{ width: "90px", height: "70px" }}
                      />
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <span className="small text-muted font-bold">#{ord.id}</span>
                          <span className={`badge ${badge.className} rounded-pill px-3 py-1 font-bold`}>{badge.label}</span>
                          <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 font-semibold">
                            <ShieldCheck size={13} className="me-1" />
                            Escrow
                          </span>
                        </div>
                        <h2 className="fw-bold fs-5 mb-1 text-body">{title}</h2>
                        <div className="d-flex align-items-center gap-3 small text-muted">
                          <span>
                            <strong>{isAr ? "السعر:" : "Price:"}</strong>{" "}
                            <span className="text-success font-bold">
                              {formatServiceCurrency(ord.price || ord.amount || 0, ord.currency || "USD")}
                            </span>
                          </span>
                          <span>
                            <strong>{activeTab === "freelancer" ? (isAr ? "العميل:" : "Client:") : (isAr ? "المستقل:" : "Seller:")}</strong>{" "}
                            {otherParty || (isAr ? "مستخدم موثق" : "Verified Member")}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="fv-search-box__btn rounded-3 px-4 py-2"
                        onClick={() => navigate(`/service-orders/${ord.id}/workspace`)}
                      >
                        <MessageSquare size={16} />
                        <span>{isAr ? "مساحة العمل والتواصل 💬" : "Order Workspace 💬"}</span>
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
